import { useEffect, useState, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { 
  getReceivedProfileViewRequestsAction, 
  clearReceivedProfileViewRequestsAction,
  acceptProfileViewRequestAction,
  denyProfileViewRequestAction,
  revokeProfileViewRequestAction,
  getPrivacySettingsAction
} from "../redux/actions/userActions";
import CommonLoading from "../components/loader/CommonLoading";
import ContentWithCards from "../components/shared/ContentWithCards";
import LoadMoreButton from "../components/shared/LoadMoreButton";
import AccessTypeModal from "../components/modals/AccessTypeModal";
import SentProfileViewRequestsModal from "../components/modals/SentProfileViewRequestsModal";
import { FaEnvelope } from "react-icons/fa";
import { useToast } from "../contexts/ToastContext";
import CountdownTimer from "../components/shared/CountdownTimer";

const ReceivedProfileViewRequests = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { success: showSuccess, error: showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [isLoadMoreLoading, setIsLoadMoreLoading] = useState(false);
  const [processingRequestId, setProcessingRequestId] = useState(null);
  const sortOrder = "desc"; // Fixed sort order (newest first)
  const [accessModalOpen, setAccessModalOpen] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [sentRequestsModalOpen, setSentRequestsModalOpen] = useState(false);
  
  const receivedRequests = useSelector((state) => state.user?.receivedProfileViewRequests || []);
  const totalReceivedRequests = useSelector((state) => state.user?.totalReceivedProfileViewRequests || 0);

  const LIMIT = 25;

  useEffect(() => {
    const fetchRequests = async () => {
      setLoading(true);
      await Promise.all([
        dispatch(getReceivedProfileViewRequestsAction(null, LIMIT, 0)),
        dispatch(getPrivacySettingsAction()) // Fetch privacy settings for countdown timer
      ]);
      setLoading(false);
    };

    fetchRequests();

    return () => {
      dispatch(clearReceivedProfileViewRequestsAction());
    };
  }, [dispatch, LIMIT]);

  const handleAcceptClick = useCallback((requestId) => {
    setSelectedRequestId(requestId);
    setAccessModalOpen(true);
  }, []);

  const handleAcceptConfirm = useCallback(async (accessType, expiryValue, expiryUnit) => {
    if (!selectedRequestId || processingRequestId) return;
    
    setAccessModalOpen(false);
    setProcessingRequestId(selectedRequestId);
    try {
      const result = await dispatch(acceptProfileViewRequestAction(selectedRequestId, accessType, expiryValue, expiryUnit));
      if (result?.success) {
        showSuccess("Profile view request accepted");
        // Reload requests to update the list
        await dispatch(getReceivedProfileViewRequestsAction(null, LIMIT, 0));
      } else {
        showError(result?.error || "Failed to accept request");
      }
    } catch (error) {
      showError("Error accepting request");
    } finally {
      setProcessingRequestId(null);
      setSelectedRequestId(null);
    }
  }, [dispatch, selectedRequestId, processingRequestId, LIMIT, showSuccess, showError]);

  const handleDeny = useCallback(async (requestId) => {
    if (processingRequestId) return;
    
    setProcessingRequestId(requestId);
    try {
      const result = await dispatch(denyProfileViewRequestAction(requestId));
      if (result?.success) {
        showSuccess("Profile view request denied");
        // Reload requests to update the list
        await dispatch(getReceivedProfileViewRequestsAction(null, LIMIT, 0));
      } else {
        showError(result?.error || "Failed to deny request");
      }
    } catch (error) {
      showError("Error denying request");
    } finally {
      setProcessingRequestId(null);
    }
  }, [dispatch, processingRequestId, LIMIT, showSuccess, showError]);

  const handleRevoke = useCallback(async (requestId) => {
    if (processingRequestId) return;
    
    setProcessingRequestId(requestId);
    try {
      const result = await dispatch(revokeProfileViewRequestAction(requestId));
      if (result?.success) {
        showSuccess("Profile view access revoked successfully");
        // Reload requests to update the list
        await dispatch(getReceivedProfileViewRequestsAction(null, LIMIT, 0));
      } else {
        showError(result?.error || "Failed to revoke access");
      }
    } catch (error) {
      showError("Error revoking access");
    } finally {
      setProcessingRequestId(null);
    }
  }, [dispatch, processingRequestId, LIMIT, showSuccess, showError]);

  const handleUserClick = useCallback((user) => {
    const profileUrl = user?.profileUrl || user?._id;
    if (profileUrl) {
      navigate(`/profile/${profileUrl}`);
    }
  }, [navigate]);

  const handleLoadMore = useCallback(() => {
    setIsLoadMoreLoading(true);
    const currentLength = receivedRequests?.length || 0;
    dispatch(getReceivedProfileViewRequestsAction(null, LIMIT, currentLength)).finally(() => {
      setIsLoadMoreLoading(false);
    });
  }, [dispatch, LIMIT, receivedRequests?.length]);

  // Sort requests by newest/oldest
  const sortedRequests = useMemo(() => {
    if (!receivedRequests || receivedRequests.length === 0) return [];
    
    const sorted = [...receivedRequests].sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    });
    
    return sorted;
  }, [receivedRequests, sortOrder]);

  const displayedRequests = useMemo(() => {
    return sortedRequests.map((request) => {
      const requester = request.requester;
      if (!requester) return null;

      return (
        <div 
          key={request._id}
          className="bg-white border rounded-md w-full shadow-2xl shadow-[#f2f5fc] relative hover:shadow-lg hover:border-primary-300 transition-all duration-300"
        >
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
            <div className="hover:bg-gray-50 rounded-md p-3 -m-3 transition-colors">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                {/* Left: User info with times below name */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-2">
                    <img
                      src={requester.avatar || "/default-avatar.png"}
                      alt="Avatar"
                      className="w-10 h-10 rounded-full object-cover flex-shrink-0 mt-0.5 cursor-pointer"
                      onClick={() => handleUserClick(requester)}
                    />
                    <div className="flex-1 min-w-0">
                      <div 
                        className="cursor-pointer mb-1"
                        onClick={() => handleUserClick(requester)}
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                          <h4 className="font-semibold text-gray-800 font-heading text-sm sm:text-base truncate">
                            {requester.name}
                          </h4>
                          <span className="text-xs text-gray-500 font-roboto-flex">
                            has requested to view your myst profile.
                          </span>
                        </div>
                      </div>
                      
                      {/* Times directly below name - aligned */}
                      <div className="flex flex-col gap-0.5">
                        <span className="text-xs text-gray-600 font-medium font-flex-regular">
                          Requested: {new Date(request.createdAt).toLocaleString("en-US", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                        {request.status === "accepted" &&
                          request.accessType === "limited" &&
                          request.expiryDate && (() => {
                            // Parse expiryDate properly
                            const expiryDate = request.expiryDate instanceof Date 
                              ? request.expiryDate 
                              : new Date(request.expiryDate);
                            
                            // Only show if expiryDate is valid
                            if (isNaN(expiryDate.getTime())) {
                              return null;
                            }
                            
                            const now = new Date();
                            const isExpired = now.getTime() >= expiryDate.getTime();
                            
                            return (
                              <span className="text-xs text-gray-600 font-medium font-flex-regular">
                                {isExpired ? "Expired: " : "Expires: "}
                                {expiryDate.toLocaleString("en-US", {
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </span>
                            );
                          })()}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Right: Status and actions - stacked vertically */}
                <div className="flex-shrink-0 self-start sm:self-auto">
                  {request.status === "pending" && (
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                      <button
                        onClick={() => handleAcceptClick(request._id)}
                        disabled={processingRequestId === request._id || accessModalOpen}
                        className="px-3 py-1.5 text-xs font-medium text-white bg-green-700 hover:bg-green-800 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                      >
                        Accept
                      </button>
                      <button
                        onClick={() => handleDeny(request._id)}
                        disabled={processingRequestId === request._id}
                        className="px-3 py-1.5 text-xs font-medium text-white bg-action hover:bg-action/90 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                      >
                        Reject
                      </button>
                    </div>
                  )}
                  {request.status === "accepted" && (() => {
                    // Check if access has expired using request's own expiry settings
                    let isExpired = false;
                    if (request.accessType === "limited" && request.expiryDate) {
                      // Parse expiryDate properly (handle both string and Date objects)
                      const expiryDate = request.expiryDate instanceof Date 
                        ? request.expiryDate 
                        : new Date(request.expiryDate);
                      
                      // Check if expiryDate is valid
                      if (isNaN(expiryDate.getTime())) {
                        // If expiryDate is invalid, don't consider it expired
                        isExpired = false;
                      } else {
                        const now = new Date();
                        // Only consider expired if expiryDate is in the past
                        // Use >= instead of > to handle exact match
                        isExpired = now.getTime() >= expiryDate.getTime();
                      }
                    }

                    return (
                      <div className="flex flex-col items-end gap-1.5">
                        {/* Top: Accepted badge */}
                        <span className="px-3 py-1 text-xs font-semibold text-green-800 bg-green-200 rounded-full whitespace-nowrap">
                          {request.accessType === "unlimited" ? "Accepted (Unlimited)" : "Accepted (Limited)"}
                        </span>
                        {/* Middle: Revoke button */}
                        {!isExpired && (
                          <button
                            onClick={() => handleRevoke(request._id)}
                            disabled={processingRequestId === request._id}
                            className="px-2 py-1 text-xs font-medium text-action bg-white border border-action/30 hover:bg-action/5 hover:border-action/50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap shadow-sm"
                          >
                            Revoke
                          </button>
                        )}
                        {/* Bottom: Expiry countdown or expired badge */}
                        {request.accessType === "limited" &&
                          request.expiryValue &&
                          request.expiryUnit && (
                          <div className="flex items-center justify-end">
                            {isExpired ? (
                              <span className="px-3 py-1 text-xs font-semibold text-action bg-action/10 rounded-full whitespace-nowrap">
                                Access Expired
                              </span>
                            ) : (
                              <CountdownTimer
                                acceptedAt={request.updatedAt}
                                expiryValue={request.expiryValue}
                                expiryUnit={request.expiryUnit}
                              />
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  {request.status === "denied" && (
                    <span className="px-3 py-1 text-xs font-semibold text-action bg-action/10 rounded-full whitespace-nowrap">
                      Denied
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }).filter(Boolean);
  }, [
    sortedRequests,
    handleAcceptClick,
    handleDeny,
    handleRevoke,
    handleUserClick,
    processingRequestId,
    accessModalOpen,
  ]);

  const shouldShowLoadMore = useMemo(() => {
    if (!receivedRequests || receivedRequests.length === 0) return false;
    const currentCount = receivedRequests?.length || 0;
    const totalCount = totalReceivedRequests || 0;
    return currentCount < totalCount && totalCount > LIMIT;
  }, [receivedRequests, totalReceivedRequests, LIMIT]);

  return (
    <>
      <SentProfileViewRequestsModal
        isOpen={sentRequestsModalOpen}
        onClose={() => setSentRequestsModalOpen(false)}
      />
      <AccessTypeModal
        isOpen={accessModalOpen}
        onClose={() => {
          setAccessModalOpen(false);
          setSelectedRequestId(null);
        }}
        onConfirm={handleAcceptConfirm}
        requestId={selectedRequestId}
      />
      <ContentWithCards>
        <div className="main-section bg-white border">
        <div className="p-3">
          {loading ? (
            <div className="flex items-center justify-center h-screen">
              <CommonLoading />
            </div>
          ) : (
            <div>
              {/* Title and Tabs */}
              <div className="mb-4 border-b py-2 pl-2 pr-2">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <h2 className="text-lg font-semibold text-gray-700">
                    Received MyST Profile Requests
                  </h2>
                  
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                    {/* View Sent Requests Link */}
                    <button
                      onClick={() => setSentRequestsModalOpen(true)}
                      className="text-sm font-medium text-primary-700 hover:text-primary-800 transition-colors font-roboto-flex whitespace-nowrap"
                    >
                      View Sent Requests
                    </button>
                    
                    {/* Tabs */}
                    <div className="flex gap-2">
                      <Link
                        to="/skills-circle/received-follow-requests"
                        className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-t-lg transition-colors ${
                          location.pathname === "/skills-circle/received-follow-requests"
                            ? "bg-primary-700 text-white"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                      >
                        Follow
                      </Link>
                      <Link
                        to="/skills-circle/received-profile-requests"
                        className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-t-lg transition-colors ${
                          location.pathname === "/skills-circle/received-profile-requests"
                            ? "bg-primary-700 text-white"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                      >
                        MyST Profile
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
              
              {receivedRequests?.length > 0 ? (
                <>
                  <div className="grid grid-cols-1 gap-3 items-start px-2">
                    {displayedRequests}
                  </div>
                  
                  {shouldShowLoadMore && (
                    <LoadMoreButton
                      onClick={handleLoadMore}
                      isLoading={isLoadMoreLoading}
                      text={`Load More Requests (${receivedRequests?.length || 0}/${totalReceivedRequests || 0})`}
                    />
                  )}
                </>
              ) : (
                <div className="text-center py-8">
                  <div className="flex justify-center items-center flex-col">
                    <FaEnvelope className="text-6xl text-gray-300 mb-4" />
                    <p className="text-gray-500 py-4 font-roboto-flex">
                      You haven't received any profile view requests yet.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </ContentWithCards>
    </>
  );
};

export default ReceivedProfileViewRequests;

